import React, { useEffect, useState } from 'react';
import { bool, func, number, string, arrayOf, object } from 'prop-types';
import { Form as FinalForm, FormSpy } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { numberAtLeast, required } from '../../../util/validators';
import { PURCHASE_CASH_NAME } from '../../../transactions/transaction';
import { types as sdkTypes } from '../../../util/sdkLoader';
import getCountryCodes from '../../../translations/countryCodes';
import * as validators from '../../../util/validators';
import visa from '../../../assets/Visa.png';
import mastercard from '../../../assets/Mastercard.png';

import klarna from '../../../assets/Klarna.png';
import { offersFeature } from '../../../config/configOffers';
import { languagesHelper as language } from '../../../helpers/languages';
import { filterCountriesByListingMarket } from '../../../util/markets';
import {
  Form,
  FieldSelect,
  FieldTextInput,
  InlineTextButton,
  PrimaryButton,
  H3,
  H6,
  SecondaryButton,
  FieldCurrencyInput,
} from '../../../components';

import { useConfiguration } from '../../../context/configurationContext';
import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';
import { GoogleAnalyticsHandler } from '../../../analytics/handlers';
import { googleAnalyticsEnabled } from '../../../config/configAnalytics';
import VariantWrapper from '../VariantWrapper';

import css from './ProductOrderForm.module.css';
import {
  BASE_CURRENCY,
  BASE_CURRENCY_SYMBOL,
  LISTING_MIN_PRICE,
  OFFER_CONSTANTS,
} from '../../../util/enums';
import { calculatePercentage } from '../../../util/genericHelpers';
import { getPriceValidators } from '../../../util/validators';
import appSettings from '../../../config/settings';
import moment from 'moment';
import { createResourceLocatorString } from '../../../util/routes';
import routeConfiguration from '../../../routing/routeConfiguration';

import { currenciesForKlarna, currencyOptions } from '../../../util/currency';
import { currenciesHelper as currency } from '../../../helpers/currencies';

// Browsers can't render huge number of select options.
// (stock is shown inside select element)
// Note: input element could allow ordering bigger quantities
const MAX_QUANTITY_FOR_DROPDOWN = 100;
const { Money } = sdkTypes;

const handleFetchLineItems = ({
  quantity,
  deliveryMethod,
  recipientCountryCode,
  listingId,
  isOwnListing,
  variantListingId,
  fetchLineItemsInProgress,
  onFetchTransactionLineItems,
  currentCurrency,
  listingCurrency,
}) => {
  const stockReservationQuantity = Number.parseInt(quantity, 10);
  const isBrowser = typeof window !== 'undefined';
  if (
    isBrowser &&
    stockReservationQuantity &&
    deliveryMethod &&
    !fetchLineItemsInProgress &&
    recipientCountryCode
  ) {
    onFetchTransactionLineItems({
      orderData: {
        stockReservationQuantity,
        deliveryMethod,
        recipientCountryCode,
        variantListingId,
      },
      listingId,
      isOwnListing,
      currentCurrency,
      listingCurrency,
    });
  }
};

const SubmitButton = ({
  isOffersModalOpen,
  isMobileLayout,
  submitInProgress,
  submitDisabled,
  hasStock,
}) => {
  if (isOffersModalOpen && isMobileLayout) {
    return null;
  }

  return (
    <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
      {hasStock ? (
        <FormattedMessage id="ProductOrderForm.ctaButton" />
      ) : (
        <FormattedMessage id="ProductOrderForm.ctaButtonNoStock" />
      )}
    </PrimaryButton>
  );
};

const OfferButton = ({
  isMobileLayout,
  isOffersModalOpen,
  submitDisabled,
  values,
  onToggleOffersModal,
  onSendOfferRequest,
  sendInquiryInProgress,
  formApi,
}) => {
  const handleOfferClick = e => {
    const { recipientCountryOrder } = values || {};
    if (!recipientCountryOrder) {
      e.preventDefault();
      // Trigger validation and focus on the country selector
      formApi.blur('recipientCountryOrder');
      formApi.focus('recipientCountryOrder');
      e.target.blur();
    } else {
      onToggleOffersModal(true);
    }
  };

  if (isMobileLayout && isOffersModalOpen) {
    return (
      <SecondaryButton
        className={css.offerBtn}
        type="button"
        inProgress={sendInquiryInProgress}
        disabled={!values?.price} // Allow click, but handle validation
        onClick={onSendOfferRequest}
      >
        <FormattedMessage id="ProductOrderForm.makeAnOffer" />
      </SecondaryButton>
    );
  }

  if (!isMobileLayout) {
    return (
      <SecondaryButton
        className={css.offerBtn}
        type="button"
        disabled={submitDisabled} // Allow click, but handle validation
        onClick={handleOfferClick}
      >
        <FormattedMessage id="ProductOrderForm.makeAnOffer" />
      </SecondaryButton>
    );
  }

  return null;
};

const renderForm = formRenderProps => {
  const [mounted, setMounted] = useState(false);
  const {
    // FormRenderProps from final-form
    handleSubmit,
    form: formApi,

    // Custom props passed to the form component
    intl,
    formId,
    currentStock,
    hasMultipleDeliveryMethods,
    listingId,
    isOwnListing,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    price,
    marketplaceName,
    values,
    listingCountryCode,
    locale,
    variantListings,
    selectedVariant,
    setSelectedVariant,
    isMobileLayout,
    isOffersModalOpen,
    onSendInquiry,
    sendInquiryInProgress,
    history,
    currentUser,
    onToggleOffersModal = () => {},
    shippingTo,
    currentListing,
  } = formRenderProps;

  const config = useConfiguration();

  let currentCurrency = currency.get();
  let listingCurrency = price?.currency;

  // Note: don't add custom logic before useEffect
  useEffect(() => {
    setMounted(true);

    // Side-effect: fetch line-items after mounting if possible
    const { quantity, deliveryMethod } = values;
    const recipientCountryCode = listingCountryCode;
    if (quantity && !formRenderProps.hasMultipleDeliveryMethods) {
      handleFetchLineItems({
        quantity,
        deliveryMethod,
        recipientCountryCode,
        listingId,
        isOwnListing,
        fetchLineItemsInProgress,
        onFetchTransactionLineItems,
        currentCurrency,
        listingCurrency,
      });
    }
  }, []);

  const countryCodes = getCountryCodes(locale);

  // Use variant's restrictions if available, otherwise fallback to listing
  const filteredCountryCodes = filterCountriesByListingMarket(
    countryCodes,
    selectedVariant || currentListing,
    selectedVariant?.attributes?.publicData?.shippingTo || shippingTo
  );

  // Auto-select first valid country when variant changes and current selection is invalid
  useEffect(() => {
    if (!mounted || !filteredCountryCodes.length) return;

    const currentCountry = formApi.getState()?.values?.recipientCountryOrder;
    const isCurrentValid = filteredCountryCodes.some(c => c.code === currentCountry);

    if (!isCurrentValid && filteredCountryCodes[0]) {
      formApi.change('recipientCountryOrder', filteredCountryCodes[0].code);
    }
  }, [selectedVariant, mounted]);

  const onSendOfferRequest = async () => {
    const { price } = values || {};
    const offerPrice = Number(price?.amount / 100).toFixed(2);
    const currencyLabel =
      currencyOptions?.find(c => c.key === currentCurrency)?.label || BASE_CURRENCY_SYMBOL;

    await onSendInquiry(
      selectedVariant,
      intl.formatMessage(
        { id: 'OfferModal.sendOffferMessage' },
        { offerPrice, currency: currencyLabel }
      ),
      {
        offerPrice,
        authorId: currentUser?.id?.uuid,
        createdAt: moment().unix(),
        type: OFFER_CONSTANTS.INITIAL,
        variant: selectedVariant,
      }
    );
    history.push(createResourceLocatorString('OffersPage', routeConfiguration(), {}, {}));
  };

  // If form values change, update line-items for the order breakdown
  const handleOnChange = formValues => {
    const {
      quantity,
      deliveryMethod,
      variantListingId = null,
      recipientCountryOrder: recipientCountryCode,
    } = formValues.values;

    if (mounted) {
      handleFetchLineItems({
        quantity,
        deliveryMethod,
        recipientCountryCode,
        listingId,
        isOwnListing,
        variantListingId,
        fetchLineItemsInProgress,
        onFetchTransactionLineItems,
        currentCurrency,
        listingCurrency,
      });
    }
  };

  // In case quantity and deliveryMethod are missing focus on that select-input.
  // Otherwise continue with the default handleSubmit function.
  const handleFormSubmit = e => {
    const analyticsHandler = new GoogleAnalyticsHandler();
    if (googleAnalyticsEnabled == true) {
      analyticsHandler.trackCustomEvent('ShopBuyNow');
    }
    const { quantity, deliveryMethod } = values || {};
    if (!quantity || quantity < 1) {
      e.preventDefault();
      // Blur event will show validator message
      formApi.blur('quantity');
      formApi.focus('quantity');
    } else if (!deliveryMethod) {
      e.preventDefault();
      // Blur event will show validator message
      formApi.blur('deliveryMethod');
      formApi.focus('deliveryMethod');
    } else {
      handleSubmit(e);
    }
  };

  const breakdownData = {};
  const showBreakdown =
    !isOffersModalOpen &&
    breakdownData &&
    lineItems &&
    !fetchLineItemsInProgress &&
    !fetchLineItemsError;

  const showContactUser = typeof onContactUser === 'function';

  const onClickContactUser = e => {
    e.preventDefault();
    onContactUser();
  };

  const priceValidators = getPriceValidators(LISTING_MIN_PRICE, BASE_CURRENCY, intl);

  const contactSellerLink = (
    <InlineTextButton onClick={onClickContactUser}>
      <FormattedMessage id="ProductOrderForm.finePrintNoStockLinkText" />
    </InlineTextButton>
  );
  const quantityRequiredMsg = intl.formatMessage({ id: 'ProductOrderForm.quantityRequired' });

  const hasStock = currentStock && currentStock > 0;
  const selectableStock =
    currentStock > MAX_QUANTITY_FOR_DROPDOWN ? MAX_QUANTITY_FOR_DROPDOWN : currentStock;
  const quantities = hasStock ? [...Array(selectableStock).keys()].map(i => i + 1) : [];
  const hasNoStockLeft = typeof currentStock != null && currentStock === 0;
  const hasOneItemLeft = typeof currentStock != null && currentStock === 1;

  const submitInProgress = fetchLineItemsInProgress;
  const submitDisabled = !hasStock;

  const shipping_country = (
    <FieldSelect
      id={`${formId}.recipientCountryOrder`}
      name="recipientCountryOrder"
      disabled={false}
      className={css.field}
      label={intl.formatMessage({ id: 'ShippingDetails.countryLabelMarshall' })}
      validate={validators.required(intl.formatMessage({ id: 'ShippingDetails.countryRequired' }))}
    >
      <option disabled value="">
        {intl.formatMessage({ id: 'ShippingDetails.countryPlaceholder' })}
      </option>
      {filteredCountryCodes.map(country => {
        return (
          <option key={country.code} value={country.code}>
            {language.labelsTranslator(country.name, intl)}
          </option>
        );
      })}
    </FieldSelect>
  );

  const showKlarna = !!currenciesForKlarna.filter(c => c.currency === currentCurrency)?.length;

  return (
    <Form onSubmit={handleFormSubmit}>
      <FormSpy subscription={{ values: true, pristine: true }} onChange={handleOnChange} />
      {hasNoStockLeft ? null : hasOneItemLeft ? (
        <FieldTextInput
          id={`${formId}.quantity`}
          className={css.quantityField}
          name="quantity"
          type="hidden"
          validate={numberAtLeast(quantityRequiredMsg, 1)}
        />
      ) : (
        <FieldSelect
          id={`${formId}.quantity`}
          className={css.quantityField}
          name="quantity"
          disabled={!hasStock}
          label={intl.formatMessage({ id: 'ProductOrderForm.quantityLabel' })}
          validate={numberAtLeast(quantityRequiredMsg, 1)}
        >
          <option disabled value="">
            {intl.formatMessage({ id: 'ProductOrderForm.selectQuantityOption' })}
          </option>
          {quantities.map(quantity => (
            <option key={quantity} value={quantity}>
              {intl.formatMessage({ id: 'ProductOrderForm.quantityOption' }, { quantity })}
            </option>
          ))}
        </FieldSelect>
      )}

      <div className={`${css.shippingCountry}`}>{shipping_country}</div>

      <VariantWrapper
        intl={intl}
        listingId={listingId}
        variantListings={variantListings}
        formApi={formApi}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
      />

      {showBreakdown ? (
        <div className={css.breakdownWrapper}>
          <H6 as="h3" className={css.bookingBreakdownTitle}>
            <FormattedMessage id="ProductOrderForm.breakdownTitle" />
          </H6>
          <hr className={css.totalDivider} />
          <EstimatedCustomerBreakdownMaybe
            breakdownData={breakdownData}
            lineItems={lineItems}
            currency={price.currency}
            marketplaceName={marketplaceName}
            processName={PURCHASE_CASH_NAME}
          />
        </div>
      ) : null}

      {isOffersModalOpen && isMobileLayout ? (
        <div className={css.inputPrice}>
          <FieldCurrencyInput
            id="price"
            name="price"
            className={css.input}
            label={intl.formatMessage({ id: 'ProductOrderForm.priceLabel' })}
            placeholder={`${BASE_CURRENCY_SYMBOL}${calculatePercentage(price?.amount / 100, 90)}`}
            currencyConfig={appSettings.getCurrencyFormatting(config.currency)}
            validate={priceValidators}
          />
        </div>
      ) : null}

      <div className={css.submitButton}>
        <SubmitButton
          isOffersModalOpen={isOffersModalOpen}
          isMobileLayout={isMobileLayout}
          submitInProgress={submitInProgress}
          submitDisabled={submitDisabled}
          hasStock={hasStock}
        />
        {offersFeature && (
          <OfferButton
            isMobileLayout={isMobileLayout}
            isOffersModalOpen={isOffersModalOpen}
            submitDisabled={submitDisabled}
            values={values}
            sendInquiryInProgress={sendInquiryInProgress}
            onToggleOffersModal={onToggleOffersModal}
            onSendOfferRequest={onSendOfferRequest}
            formApi={formApi}
          />
        )}
      </div>
      {!hasStock && showContactUser && (
        <p className={css.finePrint}>
          <FormattedMessage id="ProductOrderForm.finePrintNoStock" />
        </p>
      )}
      <div className={css.iconsRow}>
        <img src={visa} alt="Visa" width="58" height="40" />
        <img src={mastercard} alt="Mastercard" width="58" height="40" />
        {showKlarna && <img src={klarna} alt="Klarna" width="58" height="40" />}
      </div>
    </Form>
  );
};

const ProductOrderForm = props => {
  const intl = useIntl();
  const { price, currentStock, pickupEnabled, shippingEnabled, listingCountryCode, locale } = props;
  // Should not happen for listings that go through EditListingWizard.
  // However, this might happen for imported listings.
  if (!pickupEnabled && !shippingEnabled) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProductOrderForm.noDeliveryMethodSet" />
      </p>
    );
  }

  const hasOneItemLeft = currentStock && currentStock === 1;
  const quantityMaybe = hasOneItemLeft ? { quantity: '1' } : {};
  const singleDeliveryMethodAvailableMaybe =
    shippingEnabled && !pickupEnabled
      ? { deliveryMethod: 'shipping' }
      : !shippingEnabled && pickupEnabled
      ? { deliveryMethod: 'pickup' }
      : {};
  const hasMultipleDeliveryMethods = pickupEnabled && shippingEnabled;
  const initialValues = {
    ...quantityMaybe,
    ...singleDeliveryMethodAvailableMaybe,
    ...{ recipientCountryOrder: listingCountryCode },
  };

  return (
    <FinalForm
      initialValues={initialValues}
      hasMultipleDeliveryMethods={hasMultipleDeliveryMethods}
      {...props}
      intl={intl}
      render={renderForm}
    />
  );
};

ProductOrderForm.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  currentStock: null,
  listingId: null,
  isOwnListing: false,
  lineItems: null,
  fetchLineItemsError: null,
  shippingTo: [],
  currentListing: null,
};

ProductOrderForm.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,

  // form
  formId: string.isRequired,
  onSubmit: func.isRequired,

  // listing
  listingId: propTypes.uuid,
  price: propTypes.money,
  currentStock: number,
  isOwnListing: bool,
  shippingTo: arrayOf(string),
  currentListing: object,

  // line items
  lineItems: propTypes.lineItems,
  onFetchTransactionLineItems: func.isRequired,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // other
  onContactUser: func,
};

export default ProductOrderForm;
